import "@/App.css";
import "aos/dist/aos.css";
import AOS from "aos";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { Navbar } from "@/components/storefront/Navbar";
import { Footer } from "@/components/storefront/Footer";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import CartPage from "@/pages/CartPage";
import Checkout from "@/pages/Checkout";
import WishlistPage from "@/pages/WishlistPage";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Wholesale from "@/pages/Wholesale";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminWholesale from "@/pages/admin/AdminWholesale";
import AdminAttributes from "@/pages/admin/AdminAttributes";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminInvoiceSettings from "@/pages/admin/AdminInvoiceSettings";
import AdminPages from "@/pages/admin/AdminPages";
import AdminFAQ from "@/pages/admin/AdminFAQ";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminActivityLogs from "@/pages/admin/AdminActivityLogs";
import PolicyPage from "@/pages/PolicyPage";
import FAQPage from "@/pages/FAQPage";

const Storefront = ({ children }) => {
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className={`flex-1 ${isHome ? "" : "pt-[70px]"}`}>{children}</div>
      <Footer />
    </div>
  );
};

const AosManager = () => {
  const loc = useLocation();
  useEffect(() => {
    AOS.init({ duration: 900, once: true, easing: "ease-out-cubic", offset: 60 });
  }, []);
  useEffect(() => {
    const t = setTimeout(() => AOS.refreshHard(), 250);
    return () => clearTimeout(t);
  }, [loc.pathname]);
  return null;
};

const Preloader = () => {
  const [gone, setGone] = useState(() => {
    try { return sessionStorage.getItem("bc_preloaded") === "1"; } catch { return false; }
  });
  useEffect(() => {
    if (gone) return;
    const t = setTimeout(() => {
      setGone(true);
      try { sessionStorage.setItem("bc_preloaded", "1"); } catch {}
    }, 550);
    return () => clearTimeout(t);
  }, [gone]);
  if (gone) return null;
  return (
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center bg-[hsl(var(--ivory))] transition-opacity duration-300`} data-testid="preloader">
      <div className="text-center">
        <div className="font-logo text-3xl tracking-[0.2em] text-foreground bc-pulse">BODYCRUSH</div>
        <div className="mt-4 h-[2px] w-24 mx-auto bg-border overflow-hidden">
          <div className="h-full bg-primary bc-loadbar" />
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <WishlistProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Preloader />
            <AosManager />
            <Toaster position="top-center" richColors />
            <Routes>
              <Route path="/" element={<Storefront><Home /></Storefront>} />
              <Route path="/shop" element={<Storefront><Shop /></Storefront>} />
              <Route path="/product/:slug" element={<Storefront><ProductDetail /></Storefront>} />
              <Route path="/cart" element={<Storefront><CartPage /></Storefront>} />
              <Route path="/checkout" element={<Storefront><Checkout /></Storefront>} />
              <Route path="/wishlist" element={<Storefront><WishlistPage /></Storefront>} />
              <Route path="/login" element={<Storefront><Login /></Storefront>} />
              <Route path="/account" element={<Storefront><Account /></Storefront>} />
              <Route path="/wholesale" element={<Storefront><Wholesale /></Storefront>} />
              <Route path="/policies/:slug" element={<Storefront><PolicyPage /></Storefront>} />
              <Route path="/faqs" element={<Storefront><FAQPage /></Storefront>} />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="wholesale" element={<AdminWholesale />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="invoices" element={<AdminInvoiceSettings />} />
                <Route path="attributes" element={<AdminAttributes />} />
                <Route path="banners" element={<AdminBanners />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="pages" element={<AdminPages />} />
                <Route path="faqs" element={<AdminFAQ />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="activity-logs" element={<AdminActivityLogs />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </WishlistProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
